import { SharedMapSettingsService } from '../service/shared-map-settings.service';
import { CartographyResource } from '../model/cartography-resource/cartography-resource';
import { CartographyResourceBbx } from '../model/cartography-resource/cartography-resource-bbx';
import { CartographyResourceMat } from '../model/cartography-resource/cartography-resource-mat';

const BACKGROUND_SVG = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 22.35" width="100%" height="100%">
      <path fill="#2f373a" stroke="#c8c8c8" stroke-miterlimit="10"
        d="M2.5.5h11c1.1,0,2,.9,2,2v11c0,1.1-.9,2-2,2l-5.75,6-5.25-6c-1.1,0-2-.9-2-2V2.5C.5,1.4,1.4.5,2.5.5Z"/>
    </svg>
  `;

export type GenerateMarkerContentArgs = {
  label?: string[];
  icon?: string;
  color?: string;
  resource?: CartographyResource;
};

const DEFAULT_WIDTH = 30;
const DEFAULT_HEIGHT = 40;
const DEFAULT_ICON_SIZE = 15;

export class MarkerContentGenerator {
  public static mapService: SharedMapSettingsService;

  public static generateMarkerContent(args: GenerateMarkerContentArgs) {
    if (args.resource && args.resource instanceof CartographyResourceBbx) {
      args = this.processCartographyResourceBbx(args);
    }
    if (args.resource && args.resource instanceof CartographyResourceMat) {
      args = this.processCartographyResourceTag(args);
    }
    const wrapper = this.getWrapper();
    this.addListeners(wrapper, args.resource);
    const iconWrapper = this.getIconWrapper();
    iconWrapper.appendChild(this.getSvgElement());
    iconWrapper.appendChild(this.getIconElement(args?.icon, args?.color));
    wrapper.appendChild(iconWrapper);
    wrapper.appendChild(this.getLabelElement(args?.label));
    return wrapper;
  }

  private static getWrapper() {
    const wrapper = document.createElement('div');
    wrapper.style.position = 'relative';
    wrapper.style.display = 'flex';
    wrapper.style.flexDirection = 'column';
    wrapper.style.alignItems = 'center';
    wrapper.style.pointerEvents = 'all';
    return wrapper;
  }

  private static getIconWrapper() {
    const wrapper = document.createElement('div');
    wrapper.style.position = 'relative';
    wrapper.style.width = `${DEFAULT_WIDTH * this.getMultiply()}px`;
    wrapper.style.height = `${DEFAULT_HEIGHT * this.getMultiply()}px`;
    return wrapper;
  }

  private static getSvgElement() {
    const svgContainer = document.createElement('div');
    svgContainer.innerHTML = BACKGROUND_SVG;
    svgContainer.style.position = 'absolute';
    svgContainer.style.top = '0';
    svgContainer.style.left = '0';
    svgContainer.style.width = '100%';
    svgContainer.style.height = '100%';
    return svgContainer;
  }

  private static getIconElement(icon: string, color: string) {
    const iconElement = document.createElement('i');
    iconElement.className = `fa-solid fa-${icon ? icon : 'car'}`;
    iconElement.style.position = 'absolute';
    iconElement.style.top = '50%';
    iconElement.style.left = '50%';
    iconElement.style.transform = 'translate(-50%, -90%)';
    iconElement.style.color = color ? color : '#ffffff';
    iconElement.style.fontSize = `${DEFAULT_ICON_SIZE * this.getMultiply()}px`;
    return iconElement;
  }

  private static getLabelElement(labels: string[]) {
    const labelElement = document.createElement('div');
    labels.forEach((label) => {
      const element = document.createElement('div');
      element.textContent = label ? label : '???';
      element.textContent += ' ';
      labelElement.appendChild(element);
    });
    labelElement.style.fontSize = '13px';
    labelElement.style.position = 'absolute';
    labelElement.style.top = this.getMultiply() * DEFAULT_HEIGHT + 3 + 'px';
    labelElement.style.fontWeight = 'bold';
    labelElement.style.color = 'white';
    // labelElement.style.position = 'relative';
    // labelElement.style.top = `100%`;
    labelElement.style.textAlign = 'center';
    labelElement.style.textShadow = `
      -1px -1px 0 black,
      0px -1px 0 black,
      1px -1px 0 black,
      -1px  0px 0 black,
      1px  0px 0 black,
      -1px  1px 0 black,
      0px  1px 0 black,
      1px  1px 0 black,
      -1px  1px 0 black,
      0px  1px 0 black,
      1px  1px 0 black,
      -1px  1px 0 black,
      0px  1px 0 black,
      1px  1px 0 black
    `;
    labelElement.style.whiteSpace = 'nowrap';
    return labelElement;
  }

  private static processCartographyResourceBbx(
    args: GenerateMarkerContentArgs,
  ): GenerateMarkerContentArgs {
    if (this.mapService.blackboxInfoWindow()) {
      args.label = this.mapService
        .blackboxInfoWindow()
        .map((key) => args.resource[key]);
    }
    args.icon = args.resource.icon;
    args.color = args.resource.iconColor;
    return args;
  }

  private static processCartographyResourceTag(
    args: GenerateMarkerContentArgs,
  ): GenerateMarkerContentArgs {
    if (this.mapService.materialsInfoWindow()) {
      args.label = this.mapService
        .materialsInfoWindow()
        .map((key) => args.resource[key]);
    }
    args.icon = args.resource.icon;
    return args;
  }

  private static getMultiply(): number {
    if (!this.mapService.markerSize()) {
      return 1;
    }
    return 1 + (this.mapService.markerSize() - 1) / 25;
  }

  private static addListeners(
    wrapper: HTMLDivElement,
    resource: CartographyResource,
  ) {
    if (!resource) {
      return;
    }
    let eventListener = {
      type: 'mouseenter',
      listener: () => {
        const component = window['desktopMapComponent'];
        component.openInfoWindowOnMouseEnter(resource);
      },
    };
    resource.eventListeners.push(eventListener);
    wrapper.addEventListener(eventListener.type, eventListener.listener);

    eventListener = {
      type: 'mouseleave',
      listener: () => {
        const component = window['desktopMapComponent'];
        component.closeInfoWindowOnMouseLeave(resource);
      },
    };
    resource.eventListeners.push(eventListener);
    wrapper.addEventListener(eventListener.type, eventListener.listener);
  }
}
